import type { AIReadinessScore } from '@/types';
import { runWithIdlePriority } from '@/utils/idle';

const READINESS_TTL_MS = 5 * 60 * 1000;

export interface CalculateReadinessOptions {
  characterId: string;
  manuscriptId: string;
}

type CachedEntry = {
  score: AIReadinessScore;
  timestamp: number;
};

type CharacterEngineModule = typeof import('./aiReadiness/characterReadinessEngine');

const buildCacheKey = (options: CalculateReadinessOptions): string =>
  `${options.manuscriptId}:${options.characterId}`;

export class AIReadinessService {
  private cache = new Map<string, CachedEntry>();
  private pending = new Map<string, Promise<AIReadinessScore>>();
  private enginePromise: Promise<CharacterEngineModule> | null = null;

  async calculateReadiness(options: CalculateReadinessOptions): Promise<AIReadinessScore> {
    const cacheKey = buildCacheKey(options);
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < READINESS_TTL_MS) {
      return cached.score;
    }

    const inflight = this.pending.get(cacheKey);
    if (inflight) {
      return inflight;
    }

    const task = runWithIdlePriority(async () => {
      const engine = await this.loadEngine();
      const score = await engine.evaluateCharacterReadiness(options);
      this.cache.set(cacheKey, { score, timestamp: Date.now() });
      return score;
    });

    this.pending.set(cacheKey, task);
    try {
      const result = await task;
      return result;
    } finally {
      this.pending.delete(cacheKey);
    }
  }

  invalidate(manuscriptId: string, characterId?: string): void {
    if (characterId) {
      this.cache.delete(buildCacheKey({ manuscriptId, characterId }));
      return;
    }

    Array.from(this.cache.keys()).forEach((key) => {
      if (key.startsWith(`${manuscriptId}:`)) {
        this.cache.delete(key);
      }
    });
  }

  private async loadEngine(): Promise<CharacterEngineModule> {
    if (!this.enginePromise) {
      this.enginePromise = import('@/services/aiReadiness/characterReadinessEngine');
    }
    return this.enginePromise;
  }
}

export const aiReadinessService = new AIReadinessService();
